function addDotsToNumber(
	num: string,
	cursorPosition: number,
	maxDecimals: number,
) {
	const commaPos = num.indexOf(",");
	const [numberWithoutDots, afterComma] = num.split(",");

	if (!numberWithoutDots) {
		return { value: "", cursorPosition: 0 };
	}

	const dotsToAdd = Math.floor((numberWithoutDots.length - 1) / 3);

	const dotsRight = Math.max(
		Math.floor((numberWithoutDots.length - cursorPosition) / 3),
		0,
	);
	const dotsLeft = dotsToAdd - dotsRight;

	const finalCursorPosition = Math.max(cursorPosition + dotsLeft, 0);

	let value = numberWithoutDots;
	for (let i = 0; i < numberWithoutDots.length + dotsToAdd; i++) {
		const indexFromRight = value.length - i;

		if (indexFromRight % 3 === 0 && i !== 0) {
			value = `${value.slice(0, i)}.${value.slice(i, value.length)}`;
			i++;
		}
	}

	value += commaPos >= 0 && maxDecimals > 0 ? "," : "";
	value += afterComma ? `${afterComma.slice(0, maxDecimals)}` : "";

	return { value, cursorPosition: finalCursorPosition };
}

function dotsToLeft(value: string, cursorPosition: number) {
	const stringUpToCursor = value.substring(0, cursorPosition);

	const dotsUpToCursor = stringUpToCursor.match(/\./g)?.length ?? 0;

	return dotsUpToCursor;
}

function replaceCharAt(str: string, index: number, newChar: string) {
	return str.substring(0, index) + newChar + str.substring(index + 1);
}

export function getInputState(
	newValue?: string,
	oldValue?: string | number,
	opts?: {
		selectionStart?: number;
		maxDecimals?: number;
		grouping?: boolean;
	},
): { newValue?: string; cursorIndex: number } {
	const { selectionStart, maxDecimals, grouping = true } = opts ?? {};

	const thousandSeparator = ".";
	const decimalSeparator = ",";

	if (!newValue) {
		return { newValue: "", cursorIndex: 0 };
	}

	if (typeof oldValue === "number") {
		oldValue = oldValue?.toString();
	}

	const lastAddedIsDot =
		newValue.length > (oldValue?.length ?? 0) &&
		selectionStart !== undefined &&
		newValue.charAt(selectionStart - 1) === thousandSeparator;
	const addedDotSupposedToBeComma =
		lastAddedIsDot && newValue.length - selectionStart <= (maxDecimals ?? 0);
	if (addedDotSupposedToBeComma) {
		newValue = replaceCharAt(newValue, selectionStart - 1, decimalSeparator);
	}

	let newWithoutInvalid = newValue.replaceAll(
		new RegExp(`[^\\d${thousandSeparator}${decimalSeparator}]`, "g"),
		"",
	);

	let numWithOneComma = newValue;
	let commasRemoved = 0;
	const lastAddedIsComma =
		newValue.length > (oldValue?.length ?? 0) &&
		selectionStart !== undefined &&
		newValue.charAt(selectionStart - 1) === decimalSeparator;
	const lastCommaIndex = newWithoutInvalid.lastIndexOf(decimalSeparator);
	const validCommaInNumber =
		lastCommaIndex >= 0 &&
		(lastCommaIndex + 1 >= newWithoutInvalid.length - (maxDecimals ?? 0) ||
			(!lastAddedIsComma && (selectionStart ?? 0) > lastCommaIndex));

	let newOnlyNumbers: string;
	if (!validCommaInNumber) {
		newWithoutInvalid = numWithOneComma.replaceAll(
			new RegExp(`[^\\d${thousandSeparator}]`, "g"),
			"",
		);
		newOnlyNumbers = numWithOneComma.replaceAll(/[^\d]/g, "");
	} else {
		const newValueBeforeComma = newValue
			.substring(0, newValue.lastIndexOf(decimalSeparator))
			.replaceAll(new RegExp(`[^\\d${thousandSeparator}]`, "g"), "");
		const newValueAfterComma = newValue.substring(
			newValue.lastIndexOf(decimalSeparator) + 1,
		);

		numWithOneComma = `${newValueBeforeComma}${decimalSeparator}${newValueAfterComma}`;
		commasRemoved = newValue.length - numWithOneComma.length;

		newWithoutInvalid = numWithOneComma.replaceAll(
			new RegExp(`[^\\d${thousandSeparator}${decimalSeparator}]`, "g"),
			"",
		);
		newOnlyNumbers = numWithOneComma.replaceAll(
			new RegExp(`[^\\d${decimalSeparator}]`, "g"),
			"",
		);
	}

	const invalidCharCount = numWithOneComma.length - newWithoutInvalid.length;
	if (newWithoutInvalid === oldValue || newOnlyNumbers === "") {
		const extraCommas = newValue.length - newWithoutInvalid.length;

		return {
			newValue: oldValue,
			cursorIndex: (selectionStart ?? 0) - extraCommas,
		};
	}

	const newCursorIndex = Math.max((selectionStart ?? 0) - invalidCharCount, 0);
	const dotCountLeft = dotsToLeft(newWithoutInvalid, newCursorIndex);
	const cursorPosWithoutDots = newCursorIndex - dotCountLeft - commasRemoved;

	let value = newOnlyNumbers;
	let cursorPosition = cursorPosWithoutDots;
	if (grouping) {
		({ value, cursorPosition } = addDotsToNumber(
			newOnlyNumbers,
			cursorPosWithoutDots,
			maxDecimals ?? 0,
		));
	}

	return {
		newValue: value,
		cursorIndex: Math.min(cursorPosition, value.length),
	};
}
